import type {
  Notification,
  NotificationDetail,
} from '@/types/notification'
import type {
  NotificationDetailSection,
  NotificationLinkTarget,
} from '../types/utils'
import {
  NOTES_FOR_LABELS,
  formatNotificationScheduleDate,
} from './userDrawerHelpers'

export const NOTIFICATION_MENTION_SUBJECT = "You were mentioned!"

const CRM_NOTE_TYPES = ['TRAINING', 'MEETING', 'TODO'] as const

function reactAdminPath(path: string): NotificationLinkTarget {
  return { kind: 'react', href: path.startsWith('/') ? path : `/${path}` }
}

/** Detail header subtitle (matches Angular right-drawer detail header). */
export function getNotificationDetailSubtitle(mailContent: Notification): string {
  const notesFor = mailContent.notes_for ?? ''
  if (CRM_NOTE_TYPES.includes(notesFor as (typeof CRM_NOTE_TYPES)[number])) {
    const label = NOTES_FOR_LABELS[notesFor] ?? notesFor
    return `Assigned you to a ${label}`
  }
  return mailContent.subject || NOTIFICATION_MENTION_SUBJECT
}

function buildResidentScopedPath(
  basePath: string,
  refId: number,
  refUserId: number | null | undefined,
  hash?: string,
): NotificationLinkTarget | null {
  if (refUserId == null) return null
  const suffix = hash ? `#${hash}` : ''
  return reactAdminPath(`${basePath}/${refId}/${refUserId}${suffix}`)
}

function buildHereLink(detail: NotificationDetail): NotificationLinkTarget | null {
  const refId = detail.fk_ref_id
  if (refId == null) return null

  const notesFor = detail.notes_for ?? ''
  const refUserId = detail.ref_user_id

  switch (notesFor) {
    case 'DEFAULT':
      return buildResidentScopedPath(
        '/defaults/default-detail',
        refId,
        refUserId,
        'notification',
      )
    case 'RESIDENT':
      return buildResidentScopedPath(
        '/resident/resident-detail',
        refId,
        refUserId,
        'notification',
      )
    case 'USER':
      return reactAdminPath(`/user-management/edit/${refId}`)
    case 'PROPERTY':
      return reactAdminPath(`/property/edit/${refId}`)
    case 'COMPANY':
      return reactAdminPath(`/company/edit/${refId}`)
    case 'APPLICATION':
      return reactAdminPath(`/application/${refId}#notification_notes`)
    case 'UNDERWRITER':
      return reactAdminPath(`/application/${refId}#notification_credit`)
    case 'TRANSFER_LOG':
      return reactAdminPath(`/transfer/${refId}`)
    case 'TRAINING':
    case 'MEETING':
    case 'TODO':
      return reactAdminPath(`/crm#${notesFor}`)
    default:
      return null
  }
}

function buildRefLink(detail: NotificationDetail): NotificationLinkTarget | null {
  if (!detail.ref_name || detail.fk_ref_id == null) return null

  const refId = detail.fk_ref_id
  const notesFor = detail.notes_for ?? ''
  const refUserId = detail.ref_user_id

  switch (notesFor) {
    case 'DEFAULT':
      return buildResidentScopedPath('/resident/resident-detail', refId, refUserId)
    case 'RESIDENT':
      return buildResidentScopedPath('/resident/resident-detail', refId, refUserId)
    case 'USER':
      return reactAdminPath(`/user-management/edit/${refId}`)
    case 'PROPERTY':
      return reactAdminPath(`/property/edit/${refId}`)
    case 'COMPANY':
      return reactAdminPath(`/company/edit/${refId}`)
    case 'APPLICATION':
    case 'UNDERWRITER':
      return reactAdminPath(`/application/${refId}`)
    case 'TRANSFER_LOG':
      return reactAdminPath(`/transfer/${refId}`)
    default:
      return null
  }
}

function getSectionLabel(notesFor: string): string | null {
  const labels: Record<string, string> = {
    DEFAULT: 'Reimbursement Detail',
    RESIDENT: 'Resident Detail',
    USER: 'User Detail',
    PROPERTY: 'Property Detail',
    COMPANY: 'Company Detail',
    APPLICATION: 'Application Detail',
    UNDERWRITER: 'Application Detail',
    TRANSFER_LOG: 'Transfer Detail',
    TRAINING: `${NOTES_FOR_LABELS.TRAINING} Detail`,
    MEETING: `${NOTES_FOR_LABELS.MEETING} Detail`,
    TODO: `${NOTES_FOR_LABELS.TODO} Detail`,
  }
  return labels[notesFor] ?? null
}

function formatScheduleLabel(isoDate: string | undefined): string {
  if (!isoDate) return ''
  const date = formatNotificationScheduleDate(isoDate, 'date')
  const time = formatNotificationScheduleDate(isoDate, 'time')
  return `${date} ${time}`.trim()
}

export function buildNotificationDetailSection(
  mailContent: Notification,
  detail: NotificationDetail,
): NotificationDetailSection {
  const notesFor = detail.notes_for ?? ''
  const hereLink = buildHereLink(detail)
  const refLink = buildRefLink(detail)
  const sectionLabel = getSectionLabel(notesFor)
  const isCrm = CRM_NOTE_TYPES.includes(notesFor as (typeof CRM_NOTE_TYPES)[number])

  const showMention = !isCrm && hereLink != null

  const crmSchedule = isCrm
    ? {
        startLabel: formatScheduleLabel(detail.details?.start_date),
        endLabel: formatScheduleLabel(detail.details?.start_date),
      }
    : null

  const bodyHtml = isCrm
    ? (detail.details?.additional_desc ?? detail.notes ?? mailContent.notes ?? '')
    : (detail.notes ?? mailContent.notes ?? '')

  return {
    subtitle: getNotificationDetailSubtitle(mailContent),
    hereLink,
    refLink,
    sectionLabel,
    showMention,
    bodyHtml,
    crmSchedule,
  }
}
